package top.wcpe.allincore.ui.runtime.ir

import top.wcpe.allincore.ui.runtime.host.UiTask
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.internal.schedule.PlatformScheduler
import kotlin.concurrent.Volatile

/**
 * DSL 脚本执行调度器。
 */
open class DslExecutor protected constructor(useScheduler: Boolean) {

    constructor() : this(true)

    private val scheduler: PlatformScheduler? =
        if (useScheduler && !synchronousMode) PlatformScheduler("AllinCore-DSL") else null

    /**
     * 提交 DSL 任务到执行线程。
     */
    open fun submit(task: UiTask) {
        val scheduler = scheduler
        if (scheduler == null) {
            try {
                task.run()
            } catch (e: Exception) {
                LOGGER.error("[ui] DSL 任务执行异常（已拦截）", e)
            }
            return
        }
        val accepted = scheduler.execute {
            try {
                task.run()
            } catch (e: Exception) {
                LOGGER.error("[ui] DSL 任务执行异常（已拦截）", e)
            }
        }
        if (!accepted) {
            LOGGER.debug("[ui] DSL 任务提交被拒绝（executor 已关闭）")
        }
    }

    /**
     * 延迟执行 DSL 任务（用于 delay() 内建函数）。
     */
    open fun schedule(task: UiTask, delayMs: Long): ScheduledTaskHandle? {
        val scheduler = scheduler ?: return null
        val handle = scheduler.schedule({
            try {
                task.run()
            } catch (e: Exception) {
                LOGGER.error("[ui] DSL 延迟任务执行异常（已拦截）", e)
            }
        }, delayMs)
        if (handle == null) {
            LOGGER.debug("[ui] DSL 延迟任务提交被拒绝（executor 已关闭）")
        }
        return handle
    }

    /**
     * 关闭调度器。session 销毁时调用。
     */
    open fun shutdown() {
        scheduler?.shutdownNow()
    }

    /**
     * 周期性调度任务（用于 timer.interval）。
     */
    open fun scheduleAtFixedRate(task: UiTask, initialMs: Long, periodMs: Long): ScheduledTaskHandle? {
        val scheduler = scheduler ?: return null
        val handle = scheduler.scheduleRepeating({
            try {
                task.run()
            } catch (e: Exception) {
                LOGGER.error("[ui] DSL 周期任务执行异常（已拦截）", e)
            }
        }, maxOf(0L, initialMs), maxOf(1L, periodMs))
        if (handle == null) {
            LOGGER.debug("[ui] DSL 周期任务提交被拒绝（executor 已关闭）")
        }
        return handle
    }

    companion object {
        private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.DslExecutor")

        /**
         * 全局开关：启用后所有 DslExecutor 实例在构造时降级为同步模式。
         */
        @Volatile
        var synchronousMode: Boolean = false

        /**
         * 启用全局同步模式。
         */
        fun enableSynchronousMode() {
            synchronousMode = true
        }

        /**
         * 同步执行器：所有任务立即在调用线程执行，用于单元测试。
         */
        fun synchronous(): DslExecutor = SynchronousDslExecutor()
    }

    /**
     * 同步执行器：所有任务立即在调用线程执行，用于单元测试。
     * 传 useScheduler=false 不再创建底层调度器（迁移前会创建一条从不使用的守护线程，顺带修正）。
     */
    private class SynchronousDslExecutor : DslExecutor(false) {
        override fun submit(task: UiTask) {
            task.run()
        }

        override fun schedule(task: UiTask, delayMs: Long): ScheduledTaskHandle? {
            task.run()
            return null
        }

        override fun scheduleAtFixedRate(task: UiTask, initialMs: Long, periodMs: Long): ScheduledTaskHandle? {
            task.run()
            return null
        }

        override fun shutdown() {
            // no-op
        }
    }
}
